import { FadeIn } from "./FadeIn";
import {
  Inbox, FileCheck2, KanbanSquare, MessageSquare, Wrench,
  UserCog, PackageSearch, LayoutDashboard, BarChart3, Blocks,
} from "lucide-react";

const SOLUTIONS = [
  { icon: Inbox, title: "Customer Enquiries", desc: "Structured enquiry systems that collect the information your team actually needs before the enquiry reaches management." },
  { icon: FileCheck2, title: "Smart Quoting", desc: "Quote workflows that can handle straightforward enquiries efficiently while escalating unusual or complex jobs for manual assessment." },
  { icon: KanbanSquare, title: "Job & Workflow Management", desc: "Track work from enquiry through approval, scheduling, production, completion and collection." },
  { icon: MessageSquare, title: "Customer Communication", desc: "Automated confirmations, quote notifications, progress updates, reminders and ready-for-collection messages." },
  { icon: Wrench, title: "Workshop & Operations", desc: "Live job stages, employee assignments, production status and management visibility." },
  { icon: UserCog, title: "Employee Systems", desc: "Employee logins, task assignment, clock-in/out, timesheets and labour recording." },
  { icon: PackageSearch, title: "Stock & Purchasing", desc: "Track materials, consumables and stock levels, receive low-stock alerts and manage supplier purchasing." },
  { icon: LayoutDashboard, title: "Management Dashboards", desc: "Give management immediate visibility over what is happening across the business and surface the items that actually require attention." },
  { icon: BarChart3, title: "Reporting", desc: "Operational reporting across customers, jobs, employees, turnaround times, workload and other important business metrics." },
  { icon: Blocks, title: "Business Integrations", desc: "Connect operational systems with established software already used by the business." },
];

export const Solutions = () => (
  <section id="solutions" className="bg-white border-b border-gray-200 scroll-mt-20" data-testid="solutions-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
      <FadeIn className="max-w-3xl">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-5">Solutions</p>
        <h2 className="font-heading font-bold tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="solutions-heading">
          One operational system. Built around your workflows.
        </h2>
        <p className="mt-5 text-base md:text-lg text-zinc-600 leading-relaxed">
          Custom business systems, workflow automation and job management technology for established
          Australian businesses — designed to work together, not as separate tools.
        </p>
      </FadeIn>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200" data-testid="solutions-cards-grid">
        {SOLUTIONS.map((s, i) => (
          <FadeIn key={s.title} delay={(i % 3) * 0.07} className="bg-white">
            <div className="p-7 h-full group hover:bg-offwhite transition-colors duration-200">
              <div className="w-10 h-10 rounded-md bg-sage-pale flex items-center justify-center group-hover:bg-forest transition-colors duration-200">
                <s.icon size={19} strokeWidth={1.75} className="text-forest group-hover:text-sage-light transition-colors duration-200" />
              </div>
              <h3 className="mt-5 font-heading font-semibold text-charcoal text-base">{s.title}</h3>
              <p className="mt-2.5 text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
            </div>
          </FadeIn>
        ))}
        <FadeIn delay={0.14} className="bg-forest sm:col-span-2 lg:col-span-2">
          <div className="p-7 h-full flex flex-col justify-between gap-6">
            <p className="font-heading font-semibold text-white text-lg leading-snug max-w-md">
              Every system is designed around how your business actually operates — not around a generic template.
            </p>
            <p className="text-sm text-sage-light">Custom development, automation, workflow design and integrations.</p>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

export const FinancialSoftware = () => (
  <section className="bg-charcoal border-b border-charcoal" data-testid="financial-software-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
      <FadeIn className="lg:col-span-7">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-sage uppercase mb-5">Financial Software</p>
        <h2 className="font-heading font-bold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="financial-heading">
          We integrate. We don&rsquo;t reinvent regulated software.
        </h2>
        <div className="mt-6 space-y-4 text-base md:text-lg text-zinc-400 leading-relaxed">
          <p>Westline Technologies does not attempt to replace specialist accounting, payroll or tax platforms.</p>
          <p>
            Where appropriate, we integrate operational systems with established platforms such as Xero or the
            software a business already relies on.
          </p>
          <p>
            This allows specialist financial and compliance functions to remain in proven software while Westline
            manages the operational workflow around them.
          </p>
        </div>
      </FadeIn>
      <FadeIn delay={0.12} className="lg:col-span-5">
        <div className="border border-[#2A2D2A] rounded-lg p-8 bg-[#161816]">
          <p className="text-[11px] font-semibold tracking-[0.24em] text-zinc-500 uppercase">Operational workflow</p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            {["Enquiry", "Quote", "Job", "Invoice"].map((step, i) => (
              <div key={step} className="flex items-center gap-3">
                <span className="text-sm font-medium text-zinc-300 border border-[#2A2D2A] rounded px-3 py-1.5">{step}</span>
                {i < 3 && <span className="text-sage">→</span>}
              </div>
            ))}
          </div>
          <div className="my-6 border-t border-dashed border-[#2A2D2A]" />
          <p className="text-[11px] font-semibold tracking-[0.24em] text-zinc-500 uppercase">Integrates with</p>
          <div className="mt-4 flex items-center gap-4">
            <span className="inline-flex items-center gap-3 bg-white rounded-md px-4 py-2.5">
              <img
                src="https://logo.clearbit.com/xero.com"
                alt="Xero"
                className="h-6 w-6 rounded"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <span className="font-heading font-bold text-[#13B5EA] text-lg tracking-tight">Xero</span>
            </span>
            <span className="text-sm text-zinc-500">or the software your business already uses</span>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);
